import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './i18n';
import './App.css';

// Pages
import HomePage from './pages/HomePage';
import LocationsPage from './pages/LocationsPage';
import LocationDetailPage from './pages/LocationDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyBookingsPage from './pages/MyBookingsPage';
import BookingDetailPage from './pages/BookingDetailPage';
import AGBPage from './pages/AGBPage';
import DatenschutzPage from './pages/DatenschutzPage';
import ImpressumPage from './pages/ImpressumPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTrailers from './pages/admin/AdminTrailers';
import AdminLocations from './pages/admin/AdminLocations';
import AdminBookings from './pages/admin/AdminBookings';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminCompetition from './pages/admin/AdminCompetition';
import AdminBlog from './pages/admin/AdminBlog';
import WettbewerbPage from './pages/WettbewerbPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import MockupGeneratorPage from './pages/MockupGeneratorPage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import OfferPage from './pages/OfferPage';
import AdminInquiries from './pages/admin/AdminInquiries';
import AdminRoles from './pages/admin/AdminRoles';
import AdminDataManagement from './pages/admin/AdminDataManagement';
import AdminLegal from './pages/admin/AdminLegal';
import AdminBackups from './components/admin/AdminBackups';
// Hinweis: Tracking (Pageviews, Lead-Erkennung, Werbe-Pixel) läuft zentral via InvictaFlow-Loader in index.html.

// Components
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isStaff, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (!isStaff) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/locations" element={<Layout><LocationsPage /></Layout>} />
      <Route path="/locations/:id" element={<Layout><LocationDetailPage /></Layout>} />
      <Route path="/checkout/:bookingId" element={<Layout><CheckoutPage /></Layout>} />
      <Route path="/payment-success" element={<Layout><PaymentSuccessPage /></Layout>} />
      <Route path="/payment-cancel" element={<Layout><PaymentCancelPage /></Layout>} />
      <Route path="/auth" element={<Layout><AuthPage /></Layout>} />
      <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
      <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<Layout><AGBPage /></Layout>} />
      <Route path="/privacy" element={<Layout><DatenschutzPage /></Layout>} />
      <Route path="/imprint" element={<Layout><ImpressumPage /></Layout>} />
      <Route path="/impressum" element={<Layout><ImpressumPage /></Layout>} />
      <Route path="/wettbewerb" element={<Layout><WettbewerbPage /></Layout>} />
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
      <Route path="/mockup-generator" element={<Layout><MockupGeneratorPage /></Layout>} />
      
      {/* Public Offer Page (token-based access) */}
      <Route path="/offer/:offerId" element={<Layout><OfferPage /></Layout>} />

      {/* Customer Portal Routes (Protected) */}
      <Route path="/my-bookings" element={
        <ProtectedRoute>
          <Layout><MyBookingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-bookings/:bookingId" element={
        <ProtectedRoute>
          <Layout><BookingDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/portal" element={
        <ProtectedRoute>
          <Layout><CustomerPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes (Admin Protected) */}
      <Route path="/admin" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
      <Route path="/admin/trailers" element={<AdminRoute><AdminLayout><AdminTrailers /></AdminLayout></AdminRoute>} />
      <Route path="/admin/locations" element={<AdminRoute><AdminLayout><AdminLocations /></AdminLayout></AdminRoute>} />
      <Route path="/admin/bookings" element={<AdminRoute><AdminLayout><AdminBookings /></AdminLayout></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminLayout><AdminAnalytics /></AdminLayout></AdminRoute>} />
      <Route path="/admin/calendar" element={<AdminRoute><AdminLayout><AdminCalendar /></AdminLayout></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><AdminLayout><AdminCustomers /></AdminLayout></AdminRoute>} />
      <Route path="/admin/audit-log" element={<AdminRoute><AdminLayout><AdminAuditLog /></AdminLayout></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminLayout><AdminSettings /></AdminLayout></AdminRoute>} />
      <Route path="/admin/competition" element={<AdminRoute><AdminLayout><AdminCompetition /></AdminLayout></AdminRoute>} />
      <Route path="/admin/blog" element={<AdminRoute><AdminLayout><AdminBlog /></AdminLayout></AdminRoute>} />
      <Route path="/admin/inquiries" element={<AdminRoute><AdminLayout><AdminInquiries /></AdminLayout></AdminRoute>} />
      <Route path="/admin/roles" element={<AdminRoute><AdminLayout><AdminRoles /></AdminLayout></AdminRoute>} />
      <Route path="/admin/data-management" element={<AdminRoute><AdminLayout><AdminDataManagement /></AdminLayout></AdminRoute>} />
      <Route path="/admin/legal" element={<AdminRoute><AdminLayout><AdminLegal /></AdminLayout></AdminRoute>} />
      <Route path="/admin/backups" element={<AdminRoute><AdminLayout><AdminBackups /></AdminLayout></AdminRoute>} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
